import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useState, useEffect, useCallback } from "react";
import type { Course, CourseWithProgressResponse, GamificationStatsResponse, UserAchievement } from "../services/api";
import { apiService } from "../services/api";
import ProtectedRoute from "../components/ProtectedRoute";
import "../css/Courses.css";
import { logger } from "../utils/logger";
import Paywall from "../components/Paywall";
import { usePaywall } from "../hooks/usePaywall";
import { useAuth } from "../hooks/useAuth";
import ConfettiEffect from "../components/ConfettiEffect";

function Courses() {
  const search = Route.useSearch();
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [courseProgress, setCourseProgress] = useState<Record<string, CourseWithProgressResponse>>({});
  const [stats, setStats] = useState<GamificationStatsResponse | null>(null);
  const [achievements, setAchievements] = useState<UserAchievement[]>([]);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDevConfetti, setShowDevConfetti] = useState(false);
  const [showStatsCelebration, setShowStatsCelebration] = useState(false);
  const [statsMessageVisible, setStatsMessageVisible] = useState(false);
  const { isPaywallVisible, setIsPaywallVisible } = usePaywall();
  const { user } = useAuth();
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 640 : false
  );

  const triggerStatsCelebration = useCallback(() => {
    setShowStatsCelebration(true);
    setStatsMessageVisible(true);
    if (typeof window !== "undefined") {
      window.setTimeout(() => setStatsMessageVisible(false), 2800);
      window.setTimeout(() => setShowStatsCelebration(false), 3400);
    }
  }, []);
  
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 640);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  

  // Check if this is a new user signup (from query param)
  const isNewUser = search?.newUser === 'true' || showDevConfetti;
  const isDev = import.meta.env.DEV;
  
  const loadCourses = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        setStatsError(null);

        // Build array of promises - only add user-specific requests if authenticated
        const promises: [
          Promise<Record<string, Course>>,
          Promise<CourseWithProgressResponse[]>,
          Promise<GamificationStatsResponse | null>,
          Promise<UserAchievement[]>
        ] = [
          apiService.getCoursesAsRecord(),
          user ? apiService.getUserCoursesProgress().catch((err) => {
            logger.warn('Failed to load user course progress', err);
            return [] as CourseWithProgressResponse[];
          }) : Promise.resolve([] as CourseWithProgressResponse[]),
          user ? apiService.getGamificationStats().catch((err) => {
            logger.warn('Failed to load gamification stats', err);
            setStatsError(err instanceof Error ? err.message : 'Failed to load gamification stats');
            return null as GamificationStatsResponse | null;
          }) : Promise.resolve(null as GamificationStatsResponse | null),
          user ? apiService.getUserAchievements().catch((err) => {
            logger.warn('Failed to load achievements', err);
            return [] as UserAchievement[];
          }) : Promise.resolve([] as UserAchievement[]),
        ];

        const [coursesData, courseProgressList, statsData, achievementsData] = await Promise.all(promises);

        setCourses(coursesData);
        logger.debug('Courses loaded from API', { courseCount: Object.keys(coursesData).length });

        const progressMap: Record<string, CourseWithProgressResponse> = {};
        courseProgressList.forEach((item) => {
          progressMap[item.id] = item;
        });
        setCourseProgress(progressMap);

        if (statsData) {
          setStats(statsData);
        }

        const sortedAchievements = achievementsData
          .slice()
          .sort((a, b) => new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime());
        setAchievements(sortedAchievements.slice(0, 6));
      } catch (err) {
        logger.error('Failed to load courses', err);
        setError(err instanceof Error ? err.message : 'Failed to load courses');
      } finally {
        setLoading(false);
      }
    }, [user]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  useEffect(() => {
    if (search?.celebrate !== '1') return;

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.setTimeout(triggerStatsCelebration, 350);
      const url = new URL(window.location.href);
      url.searchParams.delete('celebrate');
      window.history.replaceState(null, '', url.toString());
    } else {
      triggerStatsCelebration();
    }
  }, [search?.celebrate, triggerStatsCelebration]);
  
  // Listen for lab completion messages to refresh progress
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event?.data || typeof event.data !== "object") return;
      if (event.data.type === "lab:completed") {
        // Refresh course progress when a lab is completed
        loadCourses();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [loadCourses]);
  
  logger.debug('Courses component rendering', { loading, error, courseCount: Object.keys(courses).length })
  
  return (
    <ProtectedRoute>
      {isNewUser && <ConfettiEffect />}
      {isPaywallVisible && <Paywall onClose={() => setIsPaywallVisible(false)} />}
      {statsMessageVisible && (
        <div className="stats-celebration-overlay">
          <div className="stats-celebration-message">
            🎉 Course Completed! 🎉
          </div>
        </div>
      )}
      <div className="courses-container">
      {/* Top Section with Navigation and Stats */}
      {isMobileView ? (
        <div style={{ display: 'grid', gap: 16, marginBottom: 32 }}>
          <div style={{ display: 'grid', gap: 12 }}>
            <Link className="link-button" to="/" style={{ justifySelf: 'center' }}>
              ← Back to Home
            </Link>
            <div style={{ display: 'grid', gap: 12 }}>
              <Link
                to="/labs"
                className="link-button"
                style={{
                  background: 'rgba(74, 222, 128, 0.1)',
                  borderColor: 'var(--color-gold-alt-soft)',
                  color: '#4ade80',
                }}
              >
                Explore All Labs →
              </Link>
               {isDev && !isNewUser && (
                  <>
                    <button
                      onClick={triggerStatsCelebration}
                      className="link-button"
                      style={{
                        background: 'linear-gradient(135deg, #fbbf24, #f97316)',
                        border: '2px dashed rgba(255, 255, 255, 0.3)',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                    >
                      ⚡ [DEV] Test Course Celebration
                    </button>
                    <Link
                      to="/skill-tree-concept"
                      className="link-button"
                      style={{
                        background: 'linear-gradient(135deg, #22d3ee, #4ade80)',
                        border: '2px dashed rgba(255, 255, 255, 0.3)',
                        color: '#fff',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        display: 'inline-block',
                      }}
                    >
                      🌳 [DEV] Skill Tree Concept
                    </Link>
                  </>
                )}
             </div>
           </div>


          {stats && (
            <div
              className={`courses-stats-stack ${showStatsCelebration ? 'stats-card-celebrate' : ''}`}
              style={{ display: 'grid', gap: 12 }}
            >
              <div style={{ background: 'rgba(34, 211, 238, 0.12)', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22d3ee' }}>Level {stats.user.level}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 2 }}>{stats.user.progressToNextLevel}% to next</div>
              </div>
              <div style={{ background: 'rgba(74, 222, 128, 0.12)', border: '1px solid rgba(74, 222, 128, 0.25)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#4ade80' }}>{stats.user.points}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 2 }}>Total Points</div>
              </div>
              <div style={{ background: 'rgba(167, 139, 250, 0.12)', border: '1px solid rgba(167, 139, 250, 0.25)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-purple)' }}>{stats.user.streak}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 2 }}>Current Streak</div>
              </div>
              <div style={{ background: 'var(--color-pink-soft)', border: '1px solid var(--color-pink-border)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-pink)' }}>{stats.stats.completedLabs}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 2 }}>Labs Done</div>
              </div>
              <div style={{ background: 'rgba(251, 191, 36, 0.12)', border: '1px solid rgba(251, 191, 36, 0.25)', borderRadius: 8, padding: '12px 16px' }}>
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-gold-alt)' }}>{stats.stats.achievementsEarned}</div>
                <div style={{ fontSize: '0.75rem', opacity: 0.75, marginTop: 2 }}>Achievements Earned</div>
              </div>
            </div>
          )}

          <div style={{ textAlign: 'center', padding: '0 12px' }}>
            <h1 className="courses-heading" style={{ margin: 0, marginBottom: 8 }}>Available Courses</h1>
            <p style={{ opacity: 0.9, fontSize: '1.1rem', margin: 0, marginBottom: 16 }}>
              Explore our learning paths and build your AI expertise
            </p>
            <div className="coming-soon-banner" style={{ marginTop: 12, marginBottom: 12 }}>
              🚀 <span>Coming Soon</span> · New courses and features are on the way!
            </div>
            <Link
              to="/suggestions"
              style={{ fontSize: '1rem', opacity: 0.8, textDecoration: 'none', color: '#22d3ee' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '0.8';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Are you an educator? Please send us a suggestion.
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: 40 }}>
          <div className="courses-top-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
            {/* Left Side: Navigation */}
            <div className="courses-top-nav">
              <Link className="link-button" to="/">
                ← Back to Home
              </Link>
              <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link 
                  to="/labs" 
                  className="link-button"
                  style={{
                    background: "rgba(74, 222, 128, 0.1)",
                    borderColor: "var(--color-gold-alt-soft)",
                    color: "#4ade80"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(74, 222, 128, 0.2)";
                    e.currentTarget.style.borderColor = "var(--color-gold-alt-border)";
                    e.currentTarget.style.color = "#4ade80";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(74, 222, 128,.1)";
                    e.currentTarget.style.borderColor = "var(--color-gold-alt-soft)";
                    e.currentTarget.style.color = "#4ade80";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  Explore All Labs →
                </Link>
                {isDev && !isNewUser && (
                  <>
                  <button
                    onClick={() => setShowDevConfetti(true)}
                    className="link-button"
                    style={{
                      background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                      border: '2px dashed rgba(255, 255, 255, 0.3)',
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >
                    🎉 [DEV] Test Signup Confetti
                  </button>
                    <button
                     onClick={triggerStatsCelebration}
                     className="link-button"
                     style={{
                       background: 'linear-gradient(135deg, #0ea5e9, #a855f7)',
                       border: '2px dashed rgba(255, 255, 255, 0.3)',
                       color: '#fff',
                       cursor: 'pointer',
                     }}
                   >
                     ⚡ [DEV] Test Course Celebration
                   </button>
                   <Link
                     to="/skill-tree-concept"
                     className="link-button"
                     style={{
                       background: 'linear-gradient(135deg, #22d3ee, #4ade80)',
                       border: '2px dashed rgba(255, 255, 255, 0.3)',
                       color: '#fff',
                       cursor: 'pointer',
                       textDecoration: 'none',
                       display: 'inline-block',
                     }}
                   >
                     🌳 [DEV] Skill Tree Concept
                   </Link>
                   </>
                )}
              </div>
            </div>

            {/* Right Side: Stats Container */}
            {stats && (
              <div
                className={`courses-stats-stack ${showStatsCelebration ? 'stats-card-celebrate' : ''}`}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 12,
                  padding: 12,
                  width: 'fit-content',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '8px'
                }}
              >

                <div className="course-stat-card" style={{ background: 'rgba(34, 211, 238, 0.12)', border: '1px solid rgba(34, 211, 238, 0.25)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22d3ee' }}>Level {stats.user.level}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>{stats.user.progressToNextLevel}% to next</div>
                </div>
                <div className="course-stat-card" style={{ background: 'rgba(74, 222, 128, 0.12)', border: '1px solid rgba(74, 222, 128, 0.25)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#4ade80' }}>{stats.user.points}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>Total Points</div>
                </div>
                <div style={{ background: 'rgba(167, 139, 250, 0.12)', border: '1px solid rgba(167, 139, 250, 0.25)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-purple)' }}>{stats.user.streak}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>Current Streak</div>
                </div>
                <div style={{ background: 'var(--color-pink-soft)', border: '1px solid var(--color-pink-border)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-pink)' }}>{stats.stats.completedLabs}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>Labs Done</div>
                </div>
                <div style={{ background: 'rgba(251, 191, 36, 0.12)', border: '1px solid rgba(251, 191, 36, 0.25)', borderRadius: 8, padding: '10px 14px', whiteSpace: 'nowrap', gridColumn: 'span 2' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--color-gold-alt)' }}>{stats.stats.achievementsEarned}</div>
                  <div style={{ fontSize: '0.7rem', opacity: 0.75, marginTop: 2 }}>Achievements Earned</div>
                </div>
              </div>
            )}
          </div>

          {/* Header - Absolutely Centered */}
          <div style={{ position: 'absolute', top: '32px', left: '50%', transform: 'translateX(-50%)', width: 'max-content', textAlign: 'center', zIndex: 1 }}>
            <h1 className="courses-heading" style={{ margin: 0, marginBottom: 8 }}>Available Courses</h1>
            <p style={{ opacity: 0.9, fontSize: "1.1rem", margin: 0, marginBottom: 20 }}>Explore our learning paths and build your AI expertise</p>
            <div className="coming-soon-banner" style={{ marginTop: 20, marginBottom: 20 }}>
              🚀 <span>Coming Soon</span> · New courses and features are on the way!
            </div>
            <Link 
              to="/suggestions" 
              style={{ 
                fontSize: "1rem", 
                opacity: 0.8,
                textDecoration: "none",
                color: "#22d3ee"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = "1";
                e.currentTarget.style.textDecoration = "underline";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = "0.8";
                e.currentTarget.style.textDecoration = "none";
              }}
            >
              Are you an educator? Please send us a suggestion.
            </Link>
          </div>
        </div>
      )}

      {statsError && (
        <div
          style={{
            marginBottom: 32,
            background: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#fca5a5',
            padding: '12px 16px',
            borderRadius: 12,
            fontSize: '0.9rem'
          }}
        >
          Unable to load gamification stats: {statsError}
        </div>
      )}

      {/* Search Bar */}
      <div style={{ margin: '20px 0' }}>
        <input
          type="text"
          placeholder="Search courses by title or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #374151', background: '#1f2937', color: '#f3f4f6' }}
        />
      </div>
      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: '1.2rem', opacity: 0.8 }}>Loading courses...</div>
        </div>
      )}
      
      {/* Error State */}
      {error && (
        <div style={{ 
          background: 'rgba(239, 68, 68, 0.1)', 
          border: '1px solid rgba(239, 68, 68, 0.2)',
          color: '#ef4444',
          padding: 20,
          borderRadius: 8,
          marginBottom: 20
        }}>
          <h3>Error Loading Courses</h3>
          <p>{error}</p>
          <p style={{ opacity: 0.8, fontSize: '0.9rem' }}>Please check if the API server is running.</p>
        </div>
      )}
      
       {/* Course Grid */}
       {!loading && !error && (
         <ul className="courses-list">
          {Object.entries(courses)
            .filter(([, c]) => c.title.toLowerCase().includes(searchTerm.toLowerCase()) || c.description.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(([id, c]) => {
             const isLocked = c.isPremium && user?.subscription !== 'premium';
           const isFreeUser = user?.subscription !== 'premium';
           return (
           <li key={id} className="course-card" style={{ position: "relative" }}>
             {/* Premium/Free Labels */}
              {isFreeUser && (
                <div className="course-card-badge-cluster">
                  {!c.isPremium && (
                    <span className="course-badge course-badge--free">FREE</span>
                  )}
                  {c.isPremium && (
                    <span className="course-badge course-badge--premium">PREMIUM</span>
                  )}
                </div>
              )}

             
             {/* Lock Overlay */}
             {isLocked && (
               <div
                 style={{
                   position: "absolute",
                   inset: 0,
                   background:
                     "linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.75))",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   color: "white",
                   textAlign: "center",
                   padding: "1.5rem",
                   borderRadius: "12px",
                   backdropFilter: "blur(2px)",
                   zIndex: 5,
                 }}
               >
                 <div style={{ maxWidth: 420 }}>
                   <svg viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
                     <path
                       fill="currentColor"
                       d="M17 8h-1V6a4 4 0 10-8 0v2H7a2 2 0 00-2 2v8a2 2 0 002 2h10a2 2 0 002-2v-8a2 2 0 00-2-2zm-6 8a1 1 0 11-2 0 1 1 0 012 0zm3-8H10V6a2 2 0 014 0v2z"
                     />
                   </svg>
                   <h3 style={{ marginTop: "0.75rem", fontSize: "1.25rem" }}>
                     Unlock this course
                   </h3>
                   <p style={{ opacity: 0.9, marginTop: 8 }}>
                     Upgrade to premium to access this course and all premium content.
                   </p>
                   <button
                     onClick={() => setIsPaywallVisible(true)}
                     style={{
                       marginTop: 12,
                       padding: "0.6rem 1rem",
                       borderRadius: 10,
                       border: "none",
                       cursor: "pointer",
                       fontWeight: 600,
                       background: "#22d3ee",
                       color: "#0a0f1a",
                     }}
                   >
                     Upgrade Now
                   </button>
                 </div>
               </div>
             )}
             
             <div style={{ marginBottom: 16 }}>
               <Link to={`/courses/${id}`} style={{ 
                 fontSize: "1.3rem", 
                 fontWeight: 600, 
                 textDecoration: "none", 
                 color: isLocked ? "#6b7280" : "#22d3ee",
                 display: "block",
                 marginBottom: 8
               }}>
                 {c.title}
               </Link>
               <p style={{ opacity: isLocked ? 0.6 : 0.9, lineHeight: 1.6 }}>{c.description}</p>
             </div>
             
             {/* Tags */}
             <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
               {c.tags?.map((t) => (
                 <span key={t} style={{ 
                   fontSize: 12, 
                   padding: "4px 12px", 
                   borderRadius: 999, 
                   background: "rgba(74, 222, 128, 0.1)",
                   color: "#4ade80",
                    border: "1px solid var(--color-gold-alt-soft)"
                 }}>
                   {t}
                 </span>
               ))}
               {isLocked && (
                 <span style={{ 
                   fontSize: 12, 
                   padding: "4px 12px", 
                   borderRadius: 999, 
                   background: "rgba(255, 165, 0, 0.1)",
                    color: "var(--color-gold-alt)",
                    border: "1px solid var(--color-gold-alt-soft)"
                 }}>
                   🔒 Premium
                 </span>
               )}
             </div>

            {(() => {
              const progressInfo = courseProgress[id];
              const totalLabs = progressInfo?.totalLabs ?? c.labs?.length ?? 0;
              const completedLabs = progressInfo?.completedLabs ?? 0;
              const completionPercentage = progressInfo?.completionPercentage ?? (totalLabs > 0 ? Math.round((completedLabs / totalLabs) * 100) : 0);
              const courseCompleted = totalLabs > 0 && completedLabs === totalLabs;
              if (totalLabs === 0) {
                return null;
              }
              return (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    height: 8,
                    borderRadius: 999,
                    background: "rgba(255, 255, 255, 0.08)",
                    overflow: "hidden"
                  }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(100, completionPercentage)}%`,
                        background: courseCompleted ? "linear-gradient(90deg, var(--color-primary), var(--color-secondary), var(--color-purple))" : "linear-gradient(90deg, var(--color-secondary), #38bdf8, var(--color-purple))",
                        transition: "width 0.4s ease"
                      }}
                    />
                  </div>
                  <div style={{ fontSize: "0.85rem", opacity: 0.8, marginTop: 6 }}>
                    {completedLabs}/{totalLabs} labs completed · {Math.min(100, completionPercentage)}%
                    {courseCompleted ? " · Completed" : ""}
                  </div>
                </div>
              );
            })()}
            
            {/* Actions */}
            <div style={{ display: "flex", gap: 12 }}>
              <Link to={`/courses/${id}`} onClick={(e) => { if (isLocked) { e.preventDefault(); setIsPaywallVisible(true); } }} style={{
                padding: "8px 16px",
                background: "#22d3ee",
                color: "#0a0f1a",
                textDecoration: "none",
                borderRadius: 6,
                fontSize: "0.9rem",
                fontWeight: 500
              }}>
                View Course
              </Link>
              <Link to={`/courses/${id}/labs`} onClick={(e) => { if (isLocked) { e.preventDefault(); setIsPaywallVisible(true); } }} style={{
                padding: "8px 16px",
                background: "transparent",
                color: "#6b7280",
                textDecoration: "none",
                borderRadius: 6,
                border: "1px solid #374151",
                fontSize: "0.9rem"
              }}>
                Labs ({c.labs?.length || 0})
              </Link>
            </div>
          </li>
        )})}
        </ul>

      )}

      {achievements.length > 0 && (
        <div style={{ marginTop: 48 }}>
          <h2 style={{ fontSize: "1.8rem", marginBottom: 20 }}>Recent Achievements</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
            {achievements.map((achievement) => (
              <div
                key={`${achievement.id}-${achievement.earnedAt}`}
                style={{
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.08)",
                  borderRadius: 12,
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8
                }}
              >
                <div style={{ fontSize: "2rem" }}>{achievement.icon || "🏆"}</div>
                <div style={{ fontWeight: 600 }}>{achievement.displayName}</div>
                <div style={{ fontSize: "0.85rem", opacity: 0.75 }}>{achievement.description}</div>
                <div style={{ fontSize: "0.8rem", opacity: 0.6 }}>
                  Earned {new Date(achievement.earnedAt).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
            <br/><br/><br/>

      <Outlet />

      </div>
    </ProtectedRoute>
  );
}

export const Route = createFileRoute("/courses")({
  component: Courses,
  validateSearch: (search: Record<string, unknown>): { newUser?: string; celebrate?: string } => {
    return {
      newUser: typeof search.newUser === 'string' ? search.newUser : undefined,
      celebrate: typeof search.celebrate === 'string' ? search.celebrate : undefined,
    };
  },
});
